import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Loader2,
  RefreshCw,
  Search,
  X,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import type { TutorVersion } from "@/services/api.service";

import { TutorVersionDiff } from "./TutorVersionDiff";
import { TvIdPill } from "./TvIdPill";

// ── Server payload types (see src/api/assignments.ts) ───────────────────────────

type AppId = "ai-tutor" | "gateway";

interface TvAppMeta {
  status?: string;
  name?: string;
  versionNumber?: number;
  createdAt?: string;
}

interface TvBucket {
  tutorVersionId: string;
  totalCount: number;
  liveCount: number | null;
  orphanCount: number | null;
  firstAssignedAt: string | null;
  lastAssignedAt: string | null;
  assignedBy: Record<string, number>;
}

interface AssignmentsStatus {
  env: "production" | "development";
  generatedAt: string;
  dbAvailable: boolean;
  dbError: string | null;
  liveLessonCount: number | null;
  totalAssignmentRows: number;
  pinnedLive: number | null;
  unpinnedLive: number | null;
  orphanRows: number | null;
  defaults: Record<AppId, string | null>;
  byTv: TvBucket[];
  tvMeta: Record<string, Partial<Record<AppId, TvAppMeta>>>;
}

interface BackfillPreview {
  env: string;
  target: {
    tutorVersionId: string;
    name: string | null;
    aiTutorStatus: string | null;
    gatewayStatus: string | null;
  };
  blocked: boolean;
  blockReasons: string[];
  warnings: string[];
  defaults: Record<AppId, string | null>;
  // null when the lesson catalog is unreachable; the preview is blocked then.
  liveLessonCount: number | null;
  alreadyOnTarget: number | null;
  moves: { from: string; count: number }[];
}

interface JobState {
  id: string;
  kind: "backfill" | "unpin";
  env: string;
  target?: string;
  phase: "running" | "done" | "failed";
  total: number;
  done: number;
  failedCount: number;
  failures: { lessonId: string; status: number }[];
  error?: string;
}

interface LessonLookup {
  lessonId: string;
  lesson: { name: string; softDeleted: boolean } | null;
  dbError: string | null;
  assignment: { tutorVersionId: string | null; assignedBy: string | null; assignedAt: string | null } | null;
  resolves: Record<string, { tutorVersionId: string | null; source: string | null; sttPrimary: string | null }>;
}

const API = "/api/internal/admin/assignments";

// Carries the response body so callers can read fields off an error response.
// The 409 from run/unpin includes the running job's id - see startJob.
class ApiError extends Error {
  constructor(
    message: string,
    readonly body: { error?: string; jobId?: string } | null,
  ) {
    super(message);
  }
}

const fetchJson = async <T,>(url: string, init?: RequestInit): Promise<T> => {
  const res = await fetch(url, init);
  const body = (await res.json().catch(() => null)) as (T & { error?: string }) | null;
  if (!res.ok) {
    throw new ApiError(body?.error || `HTTP ${res.status}`, body as ApiError["body"]);
  }
  if (!body) throw new Error("empty response");
  return body;
};

const fmtDate = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleDateString() : "-";

interface AssignmentsViewProps {
  versions: TutorVersion[];
}

export const AssignmentsView = ({ versions }: AssignmentsViewProps) => {
  const { user } = useAuth();

  const [status, setStatus] = useState<AssignmentsStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);

  const [targetId, setTargetId] = useState<string>("");
  const [previewData, setPreviewData] = useState<BackfillPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [showDiff, setShowDiff] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [unpinOpen, setUnpinOpen] = useState(false);
  const [unpinText, setUnpinText] = useState("");

  const [job, setJob] = useState<JobState | null>(null);
  // Job ids whose completion toast already fired - one toast per job, no
  // matter which code path sees the terminal state first.
  const toastedJobsRef = useRef<Set<string>>(new Set());

  const [lookupId, setLookupId] = useState("");
  const [lookup, setLookup] = useState<LessonLookup | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  // statusLoading starts true; every state update happens inside promise
  // callbacks, never synchronously in the effect body. Refresh bumps the
  // version counter, which re-runs the effect.
  const [statusVersion, setStatusVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetchJson<AssignmentsStatus>(`${API}/status`)
      .then((s) => {
        if (cancelled) return;
        setStatus(s);
        setStatusError(null);
      })
      .catch((e: unknown) => {
        if (!cancelled) setStatusError(e instanceof Error ? e.message : "Failed to load status");
      })
      .finally(() => {
        if (!cancelled) setStatusLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [statusVersion]);

  const refreshStatus = useCallback(() => {
    setStatusLoading(true);
    setStatusVersion((v) => v + 1);
  }, []);

  const runLookup = () => {
    if (!lookupId.trim() || lookupLoading) return;
    setLookupLoading(true);
    fetchJson<LessonLookup>(`${API}/lesson?lessonId=${encodeURIComponent(lookupId.trim())}`)
      .then(setLookup)
      .catch((err) => toast.error(err instanceof Error ? err.message : "Lookup failed"))
      .finally(() => setLookupLoading(false));
  };

  // Load the dry-run preview whenever the target changes. Stale previews are
  // hidden by the render guard (preview.target must match targetId).
  useEffect(() => {
    if (!targetId) return;
    let cancelled = false;
    fetchJson<BackfillPreview>(`${API}/preview?tv=${encodeURIComponent(targetId)}`)
      .then((p) => {
        if (!cancelled) {
          setPreviewData(p);
          setPreviewError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) setPreviewError(e instanceof Error ? e.message : "Preview failed");
      });
    return () => {
      cancelled = true;
    };
  }, [targetId]);

  const finishJob = useCallback(
    (state: JobState) => {
      if (toastedJobsRef.current.has(state.id)) return;
      toastedJobsRef.current.add(state.id);
      if (state.phase === "failed") {
        toast.error(state.error || "Job failed");
      } else {
        const summary =
          state.kind === "unpin"
            ? `Unpin done: ${state.done - state.failedCount}/${state.total} rows removed`
            : `Backfill done: ${state.done - state.failedCount}/${state.total} → ${state.target}`;
        if (state.failedCount > 0) {
          const sample = state.failures
            .slice(0, 5)
            .map((f) => `${f.lessonId} (${f.status || "network"})`)
            .join(", ");
          toast.warning(
            `${summary}. ${state.failedCount} failed: ${sample}${state.failedCount > 5 ? ", …" : ""}. Run again to repair.`,
          );
        } else {
          toast.success(summary);
        }
      }
      refreshStatus();
    },
    [refreshStatus],
  );

  // Toast on the terminal state. Also covers a job that finished within
  // startJob's first fetch and never entered the poll. finishJob is
  // idempotent per job id, so re-runs are harmless.
  useEffect(() => {
    if (job && job.phase !== "running") finishJob(job);
  }, [job, finishJob]);

  // Poll with a setTimeout chain keyed on the job id: the timer is NOT torn
  // down on every setJob (the old interval was), and a job that stops
  // answering (server restart) gives up after 7 misses instead of spinning
  // forever. A terminal phase flips jobPhase, which re-runs this effect into
  // the early return; the toast effect above handles it.
  const jobId = job?.id;
  const jobPhase = job?.phase;
  useEffect(() => {
    if (!jobId || jobPhase !== "running") return;
    let cancelled = false;
    let misses = 0;
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      fetchJson<JobState>(`${API}/jobs/${jobId}`)
        .then((next) => {
          if (cancelled) return;
          misses = 0;
          setJob(next);
          if (next.phase === "running") timer = setTimeout(tick, 1500);
        })
        .catch(() => {
          if (cancelled) return;
          misses += 1;
          if (misses >= 7) {
            toast.error(
              "Job lost. The server restarted? Refresh and check the mapping table.",
            );
            setJob(null);
            refreshStatus();
          } else {
            timer = setTimeout(tick, 1500);
          }
        });
    };
    timer = setTimeout(tick, 1500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [jobId, jobPhase, refreshStatus]);

  const startJob = async (path: string, body: Record<string, unknown>) => {
    try {
      const { jobId } = await fetchJson<{ jobId: string }>(`${API}/${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      setJob(await fetchJson<JobState>(`${API}/jobs/${jobId}`));
    } catch (e) {
      // The 409 carries the running job's id. Attach to that job instead of
      // dead-ending - this is how a refreshed page finds its way back.
      const runningId = e instanceof ApiError ? e.body?.jobId : undefined;
      if (runningId) {
        try {
          setJob(await fetchJson<JobState>(`${API}/jobs/${runningId}`));
          toast.info("A job is already running. Watching it.");
          return;
        } catch {
          /* fall through to the generic error */
        }
      }
      toast.error(e instanceof Error ? e.message : "Failed to start job");
    }
  };

  // A preview is only shown for the target it was computed for.
  const preview =
    previewData && previewData.target.tutorVersionId === targetId ? previewData : null;
  const previewPending = Boolean(targetId) && !previewError && !preview;

  // All versions, newest first. Non-published targets stay selectable; the
  // preview blocks them with the reason, so the state is visible instead of hidden.
  const sortedVersions = useMemo(
    () => [...versions].sort((a, b) => b.version_number - a.version_number),
    [versions],
  );

  // The TV most live lessons are pinned to. Orphan-only rows do not count:
  // a fully-dynamic env (dev) has no bulk, so the diff and the gap banner hide.
  const bulkTvId = useMemo(() => {
    if (!status?.byTv.length) return undefined;
    const top = [...status.byTv].sort(
      (a, b) => (b.liveCount ?? b.totalCount) - (a.liveCount ?? a.totalCount),
    )[0];
    if (!top || (top.liveCount ?? top.totalCount) <= 0) return undefined;
    return top.tutorVersionId;
  }, [status]);

  const tvName = (id: string): string | undefined =>
    versions.find((v) => v.tutor_version_id === id)?.name ??
    status?.tvMeta[id]?.["ai-tutor"]?.name ??
    status?.tvMeta[id]?.gateway?.name;

  const gatewayMissing = (id: string): boolean =>
    status ? status.tvMeta[id]?.gateway?.status !== "published" : false;

  const jobRunning = Boolean(job && job.phase !== "done" && job.phase !== "failed");
  const diffAvailable =
    Boolean(bulkTvId && targetId) &&
    versions.some((v) => v.tutor_version_id === bulkTvId) &&
    versions.some((v) => v.tutor_version_id === targetId);

  if (statusLoading && !status) {
    return <Card className="p-8 text-center text-muted-foreground">Loading assignment status…</Card>;
  }
  if (statusError && !status) {
    return (
      <Card className="space-y-3 p-8 text-center">
        <p className="text-destructive">{statusError}</p>
        <Button onClick={refreshStatus}>Retry</Button>
      </Card>
    );
  }
  if (!status) return null;

  return (
    <div className="space-y-3">
      {/* Header: defaults */}
      <Card className="flex flex-row flex-wrap items-center justify-between gap-3 p-4">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          {(["ai-tutor", "gateway"] as AppId[]).map((app) => (
            <div key={app} className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">
                {app === "gateway" ? "Notepad default" : "Tutor default"}
              </span>
              <TvIdPill id={status.defaults[app] ?? undefined} />
            </div>
          ))}
          {status.defaults["ai-tutor"] !== status.defaults.gateway && (
            <span className="flex items-center gap-1 text-sm text-destructive">
              <AlertTriangle className="h-3.5 w-3.5" /> defaults diverge between the apps
            </span>
          )}
          {bulkTvId && status.defaults["ai-tutor"] && bulkTvId !== status.defaults["ai-tutor"] && (
            <span className="flex items-center gap-1 text-sm text-muted-foreground">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
              the pinned bulk (<TvIdPill id={bulkTvId} />) is behind the default
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>as of {new Date(status.generatedAt).toLocaleTimeString()}</span>
          <Button
            size="sm"
            variant="outline"
            onClick={refreshStatus}
            disabled={statusLoading}
          >
            <RefreshCw className={cn("h-3.5 w-3.5", statusLoading && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </Card>

      {!status.dbAvailable && (
        <Card className="flex flex-row items-start gap-2 border-destructive/50 p-4 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <div>
            <p className="font-medium">Lesson catalog unreachable. Degraded view.</p>
            <p className="text-muted-foreground">
              Live/orphan splits are hidden and runs are blocked until DATABASE_URL works.
              {status.dbError ? ` (${status.dbError})` : ""}
            </p>
          </div>
        </Card>
      )}

      {/* Coverage cards */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[
          { label: "Live lessons", value: status.liveLessonCount },
          { label: "Pinned (assignment row)", value: status.pinnedLive },
          { label: "Following the default", value: status.unpinnedLive },
          { label: "Orphan rows (dead lessons)", value: status.orphanRows },
        ].map((c) => (
          <Card key={c.label} className="p-4">
            <p className="text-xs text-muted-foreground">{c.label}</p>
            <p className="text-2xl font-semibold tabular-nums">{c.value ?? "-"}</p>
          </Card>
        ))}
      </div>

      {/* Backfill runner */}
      <Card className="space-y-3 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold">Backfill</h3>
            <p className="text-xs text-muted-foreground">
              Repoint every live lesson to one tutor version. The gateway parity check runs
              before any write.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
            disabled={jobRunning || !status.dbAvailable || !status.pinnedLive}
            onClick={() => {
              setUnpinText("");
              setUnpinOpen(true);
            }}
          >
            {jobRunning && job?.kind === "unpin" && (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {job.total > 0 && (
                  <span className="tabular-nums">
                    {job.done}/{job.total}
                  </span>
                )}
              </>
            )}
            Unpin all assignments…
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={targetId} onValueChange={setTargetId}>
            <SelectTrigger className="w-[480px]">
              <SelectValue placeholder="Pick the target tutor version" />
            </SelectTrigger>
            <SelectContent>
              {sortedVersions.map((v) => (
                <SelectItem key={v.tutor_version_id} value={v.tutor_version_id} className="py-1">
                  {/* Only the name shrinks; the pill and badges are shrink-0, so
                      the trigger truncates the name instead of clipping badges. */}
                  <span className="flex w-full min-w-0 items-center gap-1.5">
                    <TvIdPill id={v.tutor_version_id} />
                    <span className="min-w-0 max-w-[420px] truncate">{v.name}</span>
                    <Badge
                      className="shrink-0"
                      variant={
                        v.status === "published"
                          ? "default"
                          : v.status === "dev"
                            ? "outline"
                            : "secondary"
                      }
                    >
                      {v.status}
                    </Badge>
                    {status.defaults["ai-tutor"] === v.tutor_version_id && (
                      <Badge variant="default" className="shrink-0">default</Badge>
                    )}
                    {v.status === "published" && gatewayMissing(v.tutor_version_id) && (
                      <Badge variant="destructive" className="shrink-0 gap-1">
                        <AlertTriangle className="h-3 w-3" /> not on gateway
                      </Badge>
                    )}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {previewPending && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {diffAvailable && (
            <Button size="sm" variant="outline" onClick={() => setShowDiff((s) => !s)}>
              {showDiff ? "Hide diff" : "Show diff vs current bulk"}
            </Button>
          )}
        </div>

        {previewError && <p className="text-sm text-destructive">{previewError}</p>}

        {preview && (
          <div className="space-y-2 text-sm">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-muted-foreground">
                {preview.liveLessonCount ?? "—"} live lessons
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-muted-foreground">
                {preview.alreadyOnTarget ?? "—"} already on target
              </span>
            </div>
            {preview.blockReasons.map((r) => (
              <p key={r} className="flex items-start gap-1.5 text-destructive">
                <XCircle className="mt-0.5 h-4 w-4 shrink-0" /> {r}
              </p>
            ))}
            {preview.warnings.map((w) => (
              <p key={w} className="flex items-start gap-1.5 text-muted-foreground">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" /> {w}
              </p>
            ))}
            {preview.moves.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="text-muted-foreground">Moves:</span>
                {preview.moves.slice(0, 8).map((m) => (
                  <span key={m.from} className="flex items-center gap-1 rounded border px-2 py-0.5">
                    {m.from === "(unassigned)" ? "unassigned" : <TvIdPill id={m.from} />}
                    <span aria-hidden>→</span>
                    <TvIdPill id={preview.target.tutorVersionId} />
                    <span className="tabular-nums">{m.count}</span>
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 pt-1">
              <Button
                size="sm"
                disabled={preview.blocked || jobRunning || !status.dbAvailable}
                onClick={() => {
                  setConfirmText("");
                  setConfirmOpen(true);
                }}
              >
                {jobRunning && job?.kind === "backfill" && (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    {job.total > 0 && (
                      <span className="tabular-nums">
                        {job.done}/{job.total}
                      </span>
                    )}
                  </>
                )}
                Run backfill…
              </Button>
            </div>
          </div>
        )}

        {showDiff && diffAvailable && (
          <TutorVersionDiff
            versions={versions}
            fromId={bulkTvId}
            toId={targetId}
            caption={
              <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                What changes for pinned lessons:
                <TvIdPill id={bulkTvId} />
                <span aria-hidden>→</span>
                <TvIdPill id={targetId} />
                (current bulk vs target)
              </div>
            }
          />
        )}
      </Card>

      {/* Mapping table */}
      <Card className="p-4">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold">Current mapping</h3>
            <p className="text-xs text-muted-foreground">
              One assignment row per lesson, shared by the tutor and the notepad. Orphan rows
              belong to deleted lessons; the backfill cannot reach them.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              className="h-8 w-44"
              placeholder="Check a lesson id"
              value={lookupId}
              onChange={(e) => setLookupId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runLookup()}
            />
            <Button
              size="sm"
              variant="outline"
              disabled={lookupLoading || !lookupId.trim()}
              onClick={runLookup}
            >
              {lookupLoading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Search className="h-3.5 w-3.5" />
              )}
              Check
            </Button>
          </div>
        </div>
        {lookup && (
          <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-lg border bg-muted/40 px-3 py-2 text-xs">
            <span className="font-medium tabular-nums">lesson {lookup.lessonId}</span>
            <span className="max-w-[240px] truncate text-muted-foreground">
              {lookup.lesson
                ? `${lookup.lesson.name || "(unnamed)"}${lookup.lesson.softDeleted ? " (SOFT-DELETED)" : ""}`
                : lookup.dbError
                  ? "catalog unavailable"
                  : "not in the lesson table"}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="text-muted-foreground">pin</span>
              {lookup.assignment ? (
                <>
                  <TvIdPill id={lookup.assignment.tutorVersionId ?? undefined} />
                  <span className="text-muted-foreground">
                    by {lookup.assignment.assignedBy} on {fmtDate(lookup.assignment.assignedAt)}
                  </span>
                </>
              ) : (
                <span className="text-muted-foreground">none, follows the default</span>
              )}
            </span>
            {Object.entries(lookup.resolves).map(([app, r]) => (
              <span key={app} className="flex items-center gap-1.5">
                <Badge variant="outline">{app}</Badge>
                <TvIdPill id={r.tutorVersionId ?? undefined} />
                <span className="text-muted-foreground">{r.source}</span>
                {r.sttPrimary && <span className="text-muted-foreground">{r.sttPrimary}</span>}
              </span>
            ))}
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto h-6 px-1.5"
              onClick={() => setLookup(null)}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tutor version</TableHead>
                <TableHead className="text-right">Live</TableHead>
                <TableHead className="text-right">Orphans</TableHead>
                <TableHead>Share of pinned</TableHead>
                <TableHead>Assigned window</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {status.byTv.map((b) => {
                const shareBase = status.pinnedLive ?? status.totalAssignmentRows;
                const shareCount = b.liveCount ?? b.totalCount;
                const share = shareBase && shareCount ? (shareCount / shareBase) * 100 : 0;
                return (
                  <TableRow key={b.tutorVersionId}>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <TvIdPill id={b.tutorVersionId} />
                        <span className="max-w-[280px] truncate text-xs text-muted-foreground">
                          {tvName(b.tutorVersionId)}
                        </span>
                        {status.defaults["ai-tutor"] === b.tutorVersionId && (
                          <Badge variant="default">default</Badge>
                        )}
                        {gatewayMissing(b.tutorVersionId) && (
                          <Badge variant="destructive" className="gap-1">
                            <AlertTriangle className="h-3 w-3" /> not on gateway
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {b.liveCount ?? b.totalCount}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {b.orphanCount ?? "-"}
                    </TableCell>
                    <TableCell className="min-w-[120px]">
                      <div className="h-2 w-full rounded bg-muted">
                        <div
                          className="h-2 rounded bg-primary"
                          style={{ width: `${Math.max(share, share > 0 ? 2 : 0)}%` }}
                        />
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {fmtDate(b.firstAssignedAt)} → {fmtDate(b.lastAssignedAt)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>


      {/* Full-run confirmation */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Run the full backfill on {status.env}?</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  Every live lesson ({preview?.liveLessonCount ?? "?"}) gets pinned to{" "}
                  <TvIdPill id={targetId} />. Existing pins are overwritten. Pins are static:
                  these lessons stop following future default changes.
                </p>
                <p className="text-muted-foreground">
                  A deploy during the run abandons it partway. Run it again to finish; the
                  writes repeat safely.
                </p>
                <p className="font-medium">Type the target id to confirm:</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={targetId}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={confirmText.trim() !== targetId}
              onClick={() => {
                setConfirmOpen(false);
                void startJob("backfill", {
                  tutorVersionId: targetId,
                  assignedBy: user || "dashboard-backfill",
                });
              }}
            >
              Run backfill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unpin confirmation */}
      <Dialog open={unpinOpen} onOpenChange={setUnpinOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unpin every live lesson on {status.env}?</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  This deletes {status.pinnedLive ?? "?"} assignment rows. Those lessons then
                  follow the app default dynamically (currently{" "}
                  <TvIdPill id={status.defaults["ai-tutor"] ?? undefined} />
                  ).
                </p>
                <p className="font-medium">Type UNPIN to confirm:</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <Input
            value={unpinText}
            onChange={(e) => setUnpinText(e.target.value)}
            placeholder="UNPIN"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnpinOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={unpinText.trim() !== "UNPIN"}
              onClick={() => {
                setUnpinOpen(false);
                void startJob("unpin", {});
              }}
            >
              Unpin everything
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
